import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

// ==============================================
// App-wide toast notifications
// ==============================================
// Wrap the app once in <ToastProvider>, then call from anywhere:
//   const toast = useToast();
//   toast.success('Saved', 'Your changes were saved.');
//   toast.error('Failed', 'Could not reach the server.');

const ToastContext = createContext(null);

// No-op fallback so calling useToast outside the provider never crashes.
const NOOP = { show: () => {}, success: () => {}, error: () => {}, info: () => {} };

export const useToast = () => useContext(ToastContext) || NOOP;

let counter = 0;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const remove = useCallback((id) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const show = useCallback((type, title, message, duration = 4500) => {
    const id = ++counter;
    setToasts((list) => [...list, { id, type, title, message }]);
    if (duration) setTimeout(() => remove(id), duration);
    return id;
  }, [remove]);

  const api = useMemo(() => ({
    show: (title, message) => show('info', title, message),
    info: (title, message) => show('info', title, message),
    success: (title, message) => show('success', title, message),
    error: (title, message) => show('error', title, message),
  }), [show]);

  const accent = { success: '#059669', error: '#DC2626', info: '#6366F1' };
  const glyph = { success: '✓', error: '!', info: 'i' };

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div style={{
        position: 'fixed', top: '1.25rem', right: '1.25rem', zIndex: 9999,
        display: 'flex', flexDirection: 'column', gap: '0.6rem', maxWidth: 360,
        pointerEvents: 'none',
      }}>
        {toasts.map((t) => (
          <div key={t.id} role="alert" onClick={() => remove(t.id)} style={{
            pointerEvents: 'auto', cursor: 'pointer',
            display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
            padding: '0.9rem 1.1rem', background: '#fff', borderRadius: 14,
            border: '1px solid #E9ECF5', borderLeft: `4px solid ${accent[t.type]}`,
            boxShadow: '0 12px 32px rgba(0,0,0,0.14)', fontFamily: "'Inter', sans-serif",
            animation: 'toastIn 0.3s cubic-bezier(0.34,1.56,0.64,1)',
          }}>
            <div style={{
              width: 26, height: 26, borderRadius: '50%', flexShrink: 0, color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '0.9rem', fontWeight: 700, background: accent[t.type],
            }}>{glyph[t.type]}</div>
            <div>
              <div style={{ fontSize: '0.88rem', fontWeight: 700, color: '#0A0A0F' }}>{t.title}</div>
              {t.message && (
                <div style={{ fontSize: '0.8rem', color: '#6B6B84', lineHeight: 1.45, marginTop: 2 }}>
                  {t.message}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      <style>{`@keyframes toastIn { from { opacity: 0; transform: translateX(20px); } to { opacity: 1; transform: translateX(0); } }`}</style>
    </ToastContext.Provider>
  );
};

export default ToastProvider;
